import type { CurvaDocument } from "@/document/model/document";
import {
  inlineStyleBinding,
  resolveFillPaints,
  resolveStrokeStyles,
  resolveTextFillPaint,
  styleBindingId,
} from "@/document/model/documentStyles";
import type { SceneNode } from "@/document/model/nodes";
import {
  createSolidPaint,
  normalizeStrokeStack,
  styleWithStrokeStack,
  type Paint,
} from "@/document/model/styles";

export type SelectionPaintKind = "fill" | "stroke";

export function defaultSelectionPaint(kind: SelectionPaintKind): Paint {
  return createSolidPaint(kind === "fill" ? "#38bdf8" : "#f8fafc");
}

export function topSelectionPaint(
  document: CurvaDocument,
  node: SceneNode,
  kind: SelectionPaintKind,
): Paint | undefined {
  if (kind === "fill") {
    return node.type === "text"
      ? resolveTextFillPaint(document, node)
      : resolveFillPaints(document, node).at(-1);
  }
  return resolveStrokeStyles(document, node).at(-1)?.paint;
}

/**
 * Returns a detached local node with `paint` applied to the top-most stack
 * entry. Empty stacks get their first entry. Callers use the returned node to
 * build normal text/style commands so component overrides keep working.
 */
export function nodeWithSelectionPaint(
  document: CurvaDocument,
  node: SceneNode,
  kind: SelectionPaintKind,
  paint: Paint,
): SceneNode {
  const localNode = styleBindingId(node, kind)
    ? inlineStyleBinding(document, node, kind)
    : structuredClone(node);

  if (kind === "fill" && localNode.type === "text") {
    const existingId = localNode.text.style.fill?.id;
    return {
      ...localNode,
      text: {
        ...localNode.text,
        style: {
          ...localNode.text.style,
          fill: {
            ...structuredClone(paint),
            id: existingId ?? paint.id,
          },
        },
      },
    };
  }

  if (kind === "fill") {
    const fills = [...localNode.style.fills];
    if (fills.length === 0) {
      fills.push(structuredClone(paint));
    } else {
      const index = fills.length - 1;
      fills[index] = {
        ...structuredClone(paint),
        id: fills[index]!.id,
      };
    }
    return {
      ...localNode,
      style: {
        ...localNode.style,
        fills,
      },
    };
  }

  const strokes = normalizeStrokeStack(localNode.style);
  if (strokes.length === 0) {
    strokes.push({
      paint: structuredClone(paint),
      width: 1,
      align: "inside",
    });
  } else {
    const index = strokes.length - 1;
    const existing = strokes[index]!;
    strokes[index] = {
      paint: {
        ...structuredClone(paint),
        id: existing.paint.id,
      },
      width: existing.width ?? 1,
      align: existing.align ?? "inside",
    };
  }
  return {
    ...localNode,
    style: styleWithStrokeStack(localNode.style, strokes),
  };
}
